import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, fullName?: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  isAdmin: boolean;
  isStaff: boolean;
  isSuperAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isStaff, setIsStaff] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  const checkUserRoles = async (userId: string) => {
    try {
      const { data: roles } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId);

      if (roles) {
        const roleList = roles.map((r) => r.role);
        setIsSuperAdmin(roleList.includes("super_admin"));
        setIsAdmin(roleList.includes("admin") || roleList.includes("super_admin"));
        setIsStaff(
          roleList.includes("super_admin") ||
            roleList.includes("admin") ||
            roleList.includes("content_manager") ||
            roleList.includes("sales_team")
        );
      }
    } catch (error) {
      console.error("Error checking roles:", error);
    }
  };

  useEffect(() => {
    // Set up auth state listener first
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);

        if (session?.user) {
          // Defer role check to avoid deadlock
          setTimeout(() => {
            checkUserRoles(session.user.id);
          }, 0);
        } else {
      setIsAdmin(false);
      setIsStaff(false);
      setIsSuperAdmin(false);
    }
      }
    );

    // Then check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      if (session?.user) {
        checkUserRoles(session.user.id);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error: error as Error | null };
  };

  const signUp = async (email: string, password: string, fullName?: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/`,
        data: {
          full_name: fullName,
        },
      },
    });
    if (!error) {
      const { sendEmail, brandedEmail } = await import("@/lib/sendEmail");
      void sendEmail({
        to: email,
        subject: "Welcome to Oya Nishikigoi",
        email_type: "welcome",
        html: brandedEmail({
          heading: `Welcome${fullName ? `, ${fullName}` : ""}!`,
          bodyHtml:
            "<p>Thanks for joining Oya Nishikigoi. Browse our curated selection of premium koi and accessories any time.</p><p>If you signed up to bid on or purchase a fish, you're all set.</p>",
          ctaLabel: "Visit the Shop",
          ctaUrl: `${window.location.origin}/shop`,
        }),
      });
    }
    return { error: error as Error | null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setIsAdmin(false);
    setIsStaff(false);
    setIsSuperAdmin(false);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        signIn,
        signUp,
        signOut,
        isAdmin,
        isStaff,
        isSuperAdmin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
